import {IRect, TextDirection} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {IPublicationMetadataItem} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-base';
import {EPUBStructuralSematicsVocabulary} from './EPUBStructuralSemanticsVocabulary';
import {MediaFragmentParser} from "../utils/MediaFragmentParser";

/**
 * # EpubDataNavigationDocument
 *
 * ## RESPONSIBILITIES
 *
 * The `EpubDataNavigationDocument` represents the data navigation document of an EPUB with region based navigation.
 *
 * This class parses and exposes an API for a Region Based Navigation document.
 * It has no real Colibrio dependencies, even though it reuses the `TextDirection` enum and the `IRect` type.
 *
 * ## SPECIFICATION
 *
 * The specification is still at the old IDPF site:
 * https://idpf.org/epub/renditions/region-nav/
 *
 * ## RELATED TYPES
 *
 * - VanillaRegionBasedNavigation
 * - VanillaReaderGuidedReadingPlayer
 * - VanillaReaderGuidedReadingTimeline
 *
 * */
export class EpubDataNavigationDocument {
    navs: DataNavigationCollection[] = [];
    constructor(private _dataNavDocument: XMLDocument) {
        let navElements = _dataNavDocument.getElementsByTagName('nav');
        for (let navElement of Array.from(navElements)) {
            let nav = new DataNavigationCollection(navElement);
            if(nav) {
                this.navs.push(nav);
            }
        }
    }

    getXmlDocument(): XMLDocument {
        return this._dataNavDocument;
    }
}

export class DataNavigationCollection {
    title: string;
    regions: Region[] = [];

    constructor(private _navElement: Element) {
        this.title = this._navElement.getAttribute('title') || '';

        let regionGroupElement = this._navElement.querySelector<HTMLOListElement>('nav > ol');

        if(regionGroupElement) {
            let regionElements = regionGroupElement.querySelectorAll<HTMLLIElement>(':scope > li');

            if(regionElements.length > 0) {
                for(let regionElement of Array.from(regionElements)) {
                    let region = new Region(regionElement);
                    if (region) {
                        this.regions.push(region);
                    }
                }
            }
        }
    }
    getElement(): Element {
        return this._navElement;
    }
}

export class Region {
    metadata: IPublicationMetadataItem[] = [];
    typeName: EPUBStructuralSematicsVocabulary = EPUBStructuralSematicsVocabulary.COMICS_PANEL;
    uri: string | undefined;
    selectors: Map<string, string> = new Map();
    shape: IRect | undefined;
    regions: Region[] = [];

    constructor(private _regionLiElement: HTMLLIElement) {
        const typeNameAttribute = this._regionLiElement.getAttribute('epub:type');
        if(typeNameAttribute &&  Object.values<string>(EPUBStructuralSematicsVocabulary).includes(typeNameAttribute)) {
            this.typeName = typeNameAttribute as EPUBStructuralSematicsVocabulary
        }

        let links = this._regionLiElement.querySelectorAll(':scope > a, :scope > span > a');
        if(links.length > 0) {
            this.uri = links[0].getAttribute('href') || undefined;

            if(this.uri) {
                this.shape = MediaFragmentParser.parseSpatialMediaFragmentSelectorFromUrl(this.uri);
                this.selectors = MediaFragmentParser.parseFragmentSelectorsFromUrl(this.uri);
            }

            let metadata = links[0].querySelectorAll<HTMLMetaElement>(':scope > meta');
            if(metadata.length > 0) {
                for(let data of Array.from(metadata)) {
                    this.metadata.push({
                        property: {
                            name: data.name,
                            namespace: data.namespaceURI || ''
                        },
                        content: {
                            lang:data.lang,
                            dir: data.dir === 'rtl' ? TextDirection.RTL : TextDirection.LTR,
                            value: data.content,
                        },
                        refinedBy: [],
                        contentScheme: null

                    })
                }
            }

        }

        let subRegionOlElement = this._regionLiElement.querySelector<HTMLOListElement>(':scope > ol');

        if(subRegionOlElement) {
            let subRegions = subRegionOlElement.querySelectorAll<HTMLLIElement>(':scope > li');

            for(let regionElement of Array.from(subRegions)) {
                let region = new Region(regionElement);
                if(region) {
                    this.regions.push(region);
                }
            }
        }
    }

    getElement(): Element {
        return this._regionLiElement;
    }

}
